/**
 * 操作ログ一覧コンポーネント
 */

import { RefObject } from "react";
import {
  Paper,
  Box,
  Grid,
  Text,
  Badge,
  Group,
  Center,
  Loader,
  Stack,
  ScrollArea,
  Accordion,
  Divider,
  Pagination,
} from "@mantine/core";
import "dayjs/locale/ja";
import { OperationLog } from "../../services/operationLogService";
import { getActionColor, getActionLabel, getResourceLabel } from "../../constants/operationLogOptions";
import { OperationLogDetailPanel } from "./OperationLogDetailPanel";
import { formatTimestamp } from "../../utils/formatters";

interface OperationLogListProps {
  logs: OperationLog[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  openedIds: string[];
  onOpenedIdsChange: (ids: string[]) => void;
  logDetails: Record<string, OperationLog>;
  loadingDetails: Record<string, boolean>;
  viewportRef: RefObject<HTMLDivElement | null>;
}

/**
 * 操作ログ一覧
 */
export function OperationLogList({
  logs,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  openedIds,
  onOpenedIdsChange,
  logDetails,
  loadingDetails,
  viewportRef,
}: OperationLogListProps) {
  return (
    <Paper
      shadow="xs"
      radius="md"
      withBorder
      style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {/* ヘッダー */}
      <Box
        p="xs"
        px="md"
        bg="var(--mantine-color-default-hover)"
        style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}
      >
        <Grid gutter="xs">
          <Grid.Col span={2.2}>
            <Text fw={700} size="sm">
              日時
            </Text>
          </Grid.Col>
          <Grid.Col span={3.3}>
            <Text fw={700} size="sm">
              操作者
            </Text>
          </Grid.Col>
          <Grid.Col span={2}>
            <Text fw={700} size="sm">
              アクション
            </Text>
          </Grid.Col>
          <Grid.Col span={1.5}>
            <Text fw={700} size="sm">
              対象
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text fw={700} size="sm">
              状態／内容
            </Text>
          </Grid.Col>
        </Grid>
      </Box>

      {/* スクロール可能な一覧エリア */}
      <Box style={{ flex: 1, overflow: "hidden" }} pos="relative">
        <ScrollArea h="100%" offsetScrollbars viewportRef={viewportRef}>
          {loading && logs.length === 0 ? (
            <Center h={200}>
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="xs" c="dimmed">
                  読み込み中...
                </Text>
              </Stack>
            </Center>
          ) : logs.length === 0 ? (
            <Center h={200}>
              <Text size="sm" c="dimmed">
                該当するログはありません
              </Text>
            </Center>
          ) : (
            <Accordion
              multiple
              variant="default"
              p="md"
              value={openedIds}
              onChange={onOpenedIdsChange}
              styles={{
                item: { border: "none" },
                control: {
                  padding: "4px 12px",
                  "&:hover": { background: "var(--mantine-color-default-hover)" },
                },
                content: { padding: "0 12px 8px 12px" },
              }}
            >
              {logs.map((log) => {
                const isOpened = openedIds.includes(log.id);
                return (
                  <Accordion.Item
                    key={log.id}
                    value={log.id}
                    style={{
                      borderBottom: isOpened
                        ? "none"
                        : "1px solid var(--mantine-color-default-border)",
                      backgroundColor: isOpened
                        ? "var(--mantine-color-subtle-hover)"
                        : "transparent",
                      borderLeft: isOpened
                        ? "4px solid var(--mantine-color-blue-filled)"
                        : "4px solid transparent",
                      transition: "all 0.2s ease",
                      margin: isOpened ? "8px 0" : "0",
                      borderRadius: isOpened ? "8px" : "0",
                      boxShadow: isOpened ? "var(--mantine-shadow-sm)" : "none",
                    }}
                  >
                    <Accordion.Control>
                      <Grid gutter="xs" align="center" style={{ width: "100%" }}>
                        <Grid.Col span={2.2}>
                          <Text size="sm" truncate>
                            {formatTimestamp(log.timestamp._seconds * 1000)}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={3.3}>
                          <Text size="sm" fw={500} truncate>
                            {log.operator.email}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <Badge
                            variant="subtle"
                            color={getActionColor(log.action)}
                            size="sm"
                            radius="sm"
                            styles={{ label: { textTransform: "none" } }}
                          >
                            {getActionLabel(log.action)}
                          </Badge>
                        </Grid.Col>
                        <Grid.Col span={1.5}>
                          <Text size="sm" c="dimmed">
                            {getResourceLabel(log.resource.type)}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <Group gap="xs" wrap="nowrap">
                            <Badge
                              color={log.status === "success" ? "green" : "red"}
                              variant="light"
                              size="sm"
                              radius="xs"
                            >
                              {log.status === "success" ? "成功" : "失敗"}
                            </Badge>
                            <Text size="sm" truncate style={{ flex: 1 }}>
                              {log.description}
                            </Text>
                          </Group>
                        </Grid.Col>
                      </Grid>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <OperationLogDetailPanel
                        log={log}
                        detail={logDetails[log.id]}
                        loading={loadingDetails[log.id] || false}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </ScrollArea>
      </Box>

      <Divider />

      {/* フッター */}
      <Box p="xs" px="md">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {total > 0
              ? `全 ${total} 件中 ${(page - 1) * pageSize + 1} - ${Math.min(
                  page * pageSize,
                  total
                )} 件`
              : "0 件"}
          </Text>
          {total > pageSize && (
            <Pagination
              total={Math.ceil(total / pageSize)}
              value={page}
              onChange={onPageChange}
              size="xs"
              radius="md"
              withEdges
            />
          )}
        </Group>
      </Box>
    </Paper>
  );
}
